import { FC, useState, createContext, useContext } from 'react';
import noop from 'lodash/noop';
import { ALERT_MESSAGE } from 'config/constants';
import { useDetailsBooking } from 'lib/hooks';
import { trackUpdatedValue, trackEditDetailsPopUp, FormValues } from './tracking';
import useCreateGuestAmendment from './lib/useCreateGuestAmendment';
import useAlert from 'hooks/useAlert';

interface ContextValue {
  onSubmit: Function;
  loading: Boolean;
  complete: Boolean;
  error?: boolean;
}

const SoftAmendmentContext = createContext<ContextValue>({
  onSubmit: noop,
  loading: false,
  complete: false,
});

export const SoftAmendmentProvider: FC = ({ children }) => {
  const { booking, refetch } = useDetailsBooking();
  const { createGuestAmendment, loading, error } = useCreateGuestAmendment();
  const [complete, setComplete] = useState(false);
  const { displayAlert } = useAlert();

  // TODO: GARD-1003 : Update GA tracking helpers (e.g. trackUpdatedValue/trackEditDetailsPopUp) to support new nullable booking types.
  // For now we coerce optional fields to non-null strings to satisfy tracking helper typings.
  const bookingDetails = {
    email: booking.travelArranger.email,
    phoneNumber: booking.travelArranger.phone ?? '',
    qffNumber: booking.reservation.leadGuest.qffNumber || '',
    businessNumber: booking.travelArranger.businessRegistrationNumber,
  };

  const onSubmit = async ({ email, phoneNumber, qffNumber, businessNumber }: FormValues) => {
    const completedAmendment = await createGuestAmendment({
      bookingId: booking.id,
      phoneNumber,
      email,
      qffNumber: qffNumber || '',
      businessNumber: businessNumber || '',
    });

    if (completedAmendment?.id) {
      trackUpdatedValue(bookingDetails, { email, phoneNumber, qffNumber, businessNumber });
      trackEditDetailsPopUp();
      refetch();
      displayAlert({ type: 'success', message: ALERT_MESSAGE.CHANGE_GUEST_SUCCESS });
      setComplete(true);
    }
  };

  return <SoftAmendmentContext.Provider value={{ onSubmit, complete, loading, error }}>{children}</SoftAmendmentContext.Provider>;
};

export const useSoftAmendmentContext = () => useContext(SoftAmendmentContext);
